import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useAppSelector } from "../../../../app/hooks";
import { getParamAsInt } from "../../../../app/utils";
import RtkqResultViewer from "../../../../components/RtkqResultViewer";
import { SelectDevelopmentView } from "../../../../components/input/SelectDevelopmentView";
import Select from "../../../../components/input/Select";
import { Loading } from "../../../../components/Loading";
import { DataTablesViewModel } from "../../../../interfaces/dataTables";
import { PlagiarismAnalysisTable } from "./PlagiarismAnalysisTable";
import { PlagiarismAnalysisModel } from "./PlagiarismReportViewModels";
import {
	useLazyPlagiarismReportDataQuery,
	usePlagiarismReportQuery,
} from "./plagiarismReportApiSlice";

export default function CoursePlagiarismReport() {
	const [queryParams] = useSearchParams();
	const courseIdQueryParam = getParamAsInt(queryParams, "courseId");

	const { course } = useAppSelector((state) => state.user);

	const courseId = courseIdQueryParam ?? course?.id;

	const [view, setView] = useState("react");
	const [promptId, setPromptId] = useState<number>();
	const [plagarismData, setPlagiarismData] =
		useState<DataTablesViewModel<PlagiarismAnalysisModel>>();

	const result = usePlagiarismReportQuery({ courseId });
	const data = result.data;

	const [callGetPlagiarismData, callGetPlagiarismDataResult] = useLazyPlagiarismReportDataQuery();

	useEffect(() => {
		if (data) {
			if (!promptId && data.prompts.length > 0) {
				setPromptId(data.prompts[0].promptId);
			} else if (promptId) {
				callGetPlagiarismData({ courseId: data.courseId, promptId })
					.unwrap()
					.then((result) => {
						setPlagiarismData(result);
					})
					.catch((error: any) => {
						console.log(error);
					});
			}
		}
	}, [data, promptId]);

	return (
		<section>
			<div className="flex justify-between">
				<div className="w-3/4">
					<h1>Class Plagiarism Report</h1>
				</div>
				<SelectDevelopmentView
					value={view}
					setValue={(v) => setView(v as string)}
					simpleListBoxId="viewFilter"
				/>
			</div>
			{view == "react" && (
				<>
					<section className="mt-8">
						<h2>Plagiarism Analysis</h2>
						<p className="mt-4">
							The report below displays the percentage of plagiarism detected in a
							selected prompt and the number of sentences plagiarized in an essay. The
							plagiarism score is from the most recent version of the essay scored for
							plagiarism.
						</p>
						<Loading loading={result.isLoading} />
						{data &&
							(data.prompts.length === 0 ? (
								<div className="mt-8 mx-12 px-8 py-4 flex items-center justify-center border-2 border-gray-200 bg-gray-100">
									There are no plagarism results to analyze! Make sure at least
									one prompt in this course has plagiarism checking enabled and a
									student has submitted a draft for plagiarism assessment.
								</div>
							) : (
								<>
									<Select
										value={promptId}
										setValue={(v) => setPromptId(Number(v))}
										label="Prompt"
										simpleListBoxId="promptFilter"
										options={data?.prompts.map((p) => ({
											value: p.promptId,
											label: p.promptName,
										}))}
										simpleListBoxClasses="w-64"
									/>
									<PlagiarismAnalysisTable
										className="mt-4"
										promptId={promptId ?? 0}
										courseId={data.courseId}
										plagiarismData={plagarismData?.aaData}
									/>
								</>
							))}
					</section>
				</>
			)}
			{view == "raw" && (
				<>
					<RtkqResultViewer heading="Plagiarism Report Data" result={result} />
					<RtkqResultViewer
						heading="Plagiarism Table Data"
						result={callGetPlagiarismDataResult}
					/>
				</>
			)}
		</section>
	);
}
